import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { IconName } from '../../../icons-container/icons.type';
import { IconSize } from '../../icon/icon.component';

export type SlotType = 'primary' | 'secondary' | 'label';

export interface SlotContext {
  $implicit: {
    icon: IconName;
    size: IconSize;
    [key: string]: any;
  };
}

export interface Variant {
  theme?: 'light' | 'dark';
  tight?: boolean;
  highlighted?: boolean;
  divider?: boolean;
  heading?: boolean;
}

@Component({
  selector: 'rt-experimental-dropdown-item',
  templateUrl: './experimental-dropdown-item.component.html',
  styleUrls: ['./experimental-dropdown-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.theme-light]': '!raw && theme === "light"',
    '[class.theme-dark]': '!raw && theme === "dark"',
    '[class.theme-default]': '!raw && !theme',
    '[class.heading]': '!raw && heading',
    '[class.highlight]': '!raw && highlighted',
    '[class.tight]': '!raw && tight',
    '[class.divider]': '!raw && divider',
    '[class.raw]': 'raw',
    '[class.disable-hover-effects]': 'disableHoverEffects',
  },
})
export class ExperimentalDropdownItemComponent implements Variant {
  public readonly SlotContext: SlotContext;
  // slots
  public contexts: Map<SlotType, SlotContext> = new Map();
  public slots: Map<SlotType, TemplateRef<SlotContext> | IconName> = new Map();

  // variants
  @Input() public theme: 'light' | 'dark';
  @Input() public tight: boolean;
  @Input() public highlighted: boolean;
  @Input() public divider: boolean;
  @Input() public disableHoverEffects: boolean;
  @Input() public heading: boolean;
  @Input() public raw: boolean = false;

  // content
  @Input() public label: string | TemplateRef<any>;

  // helper setters
  @Input()
  public set icon(value: IconName) {
    this.slotPrimary = value;
  }
  @Input()
  public set iconRight(value: IconName) {
    this.slotSecondary = value;
  }

  // slot templates
  @Input()
  public set slotPrimary(value: TemplateRef<SlotContext> | IconName) {
    if (typeof value === 'string') {
      this.contexts.set('primary', { $implicit: { icon: value, size: 'small' } });
    }

    this.slots.set('primary', value);
  }

  public get slotPrimary(): TemplateRef<SlotContext> | IconName {
    return this.slots.get('primary')!;
  }

  @Input()
  public set slotPrimaryContext(value: any) {
    this.contexts.set('primary', { $implicit: value });
  }

  public get slotPrimaryContext(): any {
    return this.contexts.get('primary');
  }

  @Input()
  public set slotSecondary(value: TemplateRef<SlotContext> | IconName) {
    if (typeof value === 'string') {
      this.contexts.set('secondary', { $implicit: { icon: value, size: 'medium' } });
    }

    this.slots.set('secondary', value);
  }

  public get slotSecondary(): TemplateRef<SlotContext> | IconName {
    return this.slots.get('secondary')!;
  }

  @Input()
  public set slotSecondaryContext(value: any) {
    this.contexts.set('secondary', { $implicit: value });
  }

  public get slotSecondaryContext(): any {
    return this.contexts.get('secondary');
  }

  @Input()
  public set labelContext(value: any) {
    this.contexts.set('label', { $implicit: value });
  }

  public get labelContext(): any {
    return this.contexts.get('label');
  }

  public get labelIsTemplate(): boolean {
    return this.label instanceof TemplateRef;
  }

  constructor(private elRef: ElementRef) {}

  public isSymbolIcon(symbolTplOrIconName: TemplateRef<SlotContext> | string): boolean {
    return typeof symbolTplOrIconName === 'string';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('highlighted' in changes) {
      if (changes.highlighted.currentValue) {
        const { nativeElement } = this.elRef;

        if ('scrollIntoView' in nativeElement) {
          nativeElement.scrollIntoView({ block: 'center', inline: 'center' });
        }
      }
    }
  }
}
